import React, { useMemo } from 'react'
import PropTypes from 'helpers/proptypes'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Map } from 'immutable'
import { Form, Message } from 'semantic-ui-react'
import ApiErrorMessage from 'components/errors/ApiErrorMessage'
import { CategoriesDropdown } from 'components/forms/categories/CategoriesDropdown'
import { buildSelectOptions } from 'helpers/forms'
import { getTranslatedOptions } from 'helpers/options'
import { payoutGatewayOptions } from 'helpers/payouts'
import { hostTypologyOptions } from 'helpers/users'
import { updateUser } from 'redux/entities/actions'
import { mergeInForm } from 'redux/forms/actions'
import { getForm } from 'redux/forms/reducer'
import { getSortedCurrencies, getSortedLanguages, updatingUser, getUserUpdateError } from 'redux/entities/selectors'
import { UserDescriptionsInput } from './UserDescriptionInput'

export function UserForm({ user }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const currencies = useSelector(getSortedCurrencies)
  const languages = useSelector(getSortedLanguages)
  const isUpdatingUser = useSelector((state) => updatingUser(state, user.id))
  const updateUserError = useSelector((state) => getUserUpdateError(state, user.id))
  const form = useSelector((state) => getForm(state, 'user'))

  const hasStripeAccount = !!user.account.stripe_account_id

  const formPOJO = useMemo(() => form.toJS(), [form])

  function handleChangeForm(value) {
    dispatch(mergeInForm({ formName: 'user', value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    dispatch(updateUser(user.id, formPOJO))
  }

  if (form.size === 0) return null

  return (
    <Form onSubmit={handleSubmit} warning={hasStripeAccount} error={!!updateUserError} loading={isUpdatingUser}>
      <Form.Field>
        <label>{t('Users::Summary::User Tags')}</label>
        <CategoriesDropdown handleChange={(_, { value: tags }) => handleChangeForm({ tags })} value={formPOJO.tags} />
      </Form.Field>

      <Form.Input
        label={t('Users::Summary::Phone')}
        type='tel'
        icon='phone'
        iconPosition='left'
        value={formPOJO.phone}
        onChange={(_, { value }) => handleChangeForm({ phone: value || null })}
      />

      <Form.Select
        label={t('Users::Summary::Currency')}
        fluid
        options={buildSelectOptions(currencies)}
        disabled={hasStripeAccount}
        value={formPOJO.currency_id}
        onChange={(_, { value }) => handleChangeForm({ currency_id: value })}
      />

      <Message warning>
        <p>{t('Users::Summary::The currency cannot be updated if the user already has a stripe account defined')}</p>
      </Message>

      <Form.Dropdown
        label={t('Users::Summary::Spoken languages')}
        multiple
        selection
        fluid
        search
        options={buildSelectOptions(languages)}
        value={formPOJO.languages_ids}
        onChange={(_, { value }) => handleChangeForm({ languages_ids: value })}
      />

      {user.isHost && (
        <Form.Select
          label={t('Users::Summary::Host Typology')}
          fluid
          options={hostTypologyOptions}
          value={formPOJO.properties.filter(({ key }) => key === 'host_typology')?.[0]?.value || ''}
          onChange={(_, { value }) =>
            handleChangeForm({
              properties: [
                ...user.properties.toJS().filter((property) => property.key !== 'host_typology'),
                { key: 'host_typology', value, zone: 'private' },
              ],
            })
          }
        />
      )}
      {formPOJO.descriptions && (
        <UserDescriptionsInput
          hostLanguage={user.account.language}
          updateForm={(formDescriptions) => handleChangeForm({ descriptions: formDescriptions.toList() })}
          formDescriptions={form
            .get('descriptions')
            .reduce((acc, description) => acc.set(String(description.get('language_id')), Map(description)), Map())}
        />
      )}

      <Form.Select
        label={t('Users::Summary::Payout Gateway')}
        fluid
        options={getTranslatedOptions(t, payoutGatewayOptions)}
        value={formPOJO.payout_gateway}
        defaultValue={formPOJO.payout_gateway}
        onChange={(_, { value }) => handleChangeForm({ payout_gateway: value })}
      />

      <Form.Field style={{ textAlign: 'center' }}>
        <Form.Button>{t('Users::Summary::Submit')}</Form.Button>
      </Form.Field>

      <ApiErrorMessage error={updateUserError} modal />
    </Form>
  )
}

UserForm.propTypes = {
  user: PropTypes.immutable.record.isRequired,
}
